import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { createSupabaseClient } from "../../../hooks/useSupabase";
import useTranslations from "../../Unknown/AppContext/useTranslations";
import { useUIContext } from "../../Unknown/UIContext";
import getAppointmentOfferings, {
  DashboardAppointmentOffering,
} from "./getAppointmentOfferings";
import useSystemConfig from "../../../hooks/useSystemConfig";
import supabaseClient from "../../../common/supabaseClient";
import { useAppContext } from "../../Unknown/AppContext";

export const useTableAppointmentOfferings = (params: {
  centerIds: string[];
  isReadShown: boolean;
}) => {
  const { centerIds, isReadShown } = params;
  const supabase = useMemo(() => createSupabaseClient(), []);
  const translations = useTranslations();
  const {
    dataState: { data: config, status: configStatus },
  } = useSystemConfig();

  const { setAlert } = useUIContext();

  const { centers } = useAppContext();

  const timezone = centers.length === 1 ? centers[0].timezone : null;

  const [isAppointmentsLoading, setIsAppointmentsLoading] = useState(false);
  const [appointmentOfferings, setAppointmentOfferings] = useState<
    DashboardAppointmentOffering[]
  >([]);

  const hasDataRef = useRef(false);

  const fetch = useCallback(async () => {
    try {
      if (!supabase) return;

      if (!hasDataRef.current) {
        setIsAppointmentsLoading(true);
      }

      const unreadOfferingsData = await getAppointmentOfferings({
        supabase,
        centerIds,
        isReadShown: false,
        showReadStudiesDays: config?.show_read_studies_duration_days || 0,
        timezone,
      });

      const readOfferingsData = isReadShown
        ? await getAppointmentOfferings({
            supabase,
            centerIds,
            isReadShown: true,
            showReadStudiesDays: config?.show_read_studies_duration_days || 0,
          })
        : [];

      const combined = [...unreadOfferingsData, ...readOfferingsData];
      hasDataRef.current = combined.length > 0;
      setAppointmentOfferings(combined);
    } catch (error) {
      setAlert({
        isShown: true,
        message: translations.defaultError,
        severity: "error",
      });

      setAppointmentOfferings([]);
    } finally {
      setIsAppointmentsLoading(false);
    }
  }, [
    supabase,
    centerIds,
    config?.show_read_studies_duration_days,
    timezone,
    isReadShown,
    setAlert,
    translations.defaultError,
  ]);

  const fetchRef = useRef(fetch);
  useEffect(() => {
    fetchRef.current = fetch;
  }, [fetch]);

  useEffect(() => {
    if (configStatus === "success" && config?.show_read_studies_duration_days) {
      fetchRef.current();
    }

    const appointmentOfferingSubscription = supabaseClient
      .channel("appointment-offerings-update")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "appointment_offerings" },
        (payload) => {
          if (
            payload.new &&
            configStatus === "success" &&
            config?.show_read_studies_duration_days
          ) {
            fetchRef.current();
          }
        },
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "appointments" },
        (payload) => {
          if (
            payload.new &&
            payload.new.status !== payload.old.status &&
            configStatus === "success" &&
            config?.show_read_studies_duration_days
          ) {
            fetchRef.current();
          }
        },
      )
      .subscribe();

    return () => {
      supabaseClient.removeChannel(appointmentOfferingSubscription);
    };
  }, [isReadShown, configStatus, config?.show_read_studies_duration_days]);

  return {
    appointmentOfferings,
    isLoading: isAppointmentsLoading,
    refetch: fetch,
  };
};
